import React from "react";
import { Box, Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PROMOTION_EVENTS_TIMEZONE } from "../../../constants/promotionEvents";
import "./ActivityTrailModal.css";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "min(80vh, 720px)",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  outline: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

export const getActivityTrailInitials = (name = "") => {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const formatActivityTrailAt = (iso) => {
  if (!iso) return "—";
  const parsed = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: PROMOTION_EVENTS_TIMEZONE,
    day: "numeric",
    month: "short",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(parsed);
  const read = (type) => parts.find((part) => part.type === type)?.value || "";
  const day = read("day");
  const month = read("month");
  const year = read("year");
  const hour = read("hour");
  const minute = read("minute");
  const dayPeriod = read("dayPeriod").toUpperCase();
  return `${day} ${month} ${year}, ${hour}:${minute} ${dayPeriod}`;
};

export const activityTrailStatusMeta = (status) => {
  if (status === "INVITE_ACCEPTED") {
    return {
      label: "Invite Accepted",
      pillClass: "activity-trail-pill--accepted",
    };
  }
  if (status === "INVITE_REJECTED") {
    return {
      label: "Rejected",
      pillClass: "activity-trail-pill--rejected",
    };
  }
  if (status === "CONTACT_PENDING") {
    return {
      label: "Contact Pending",
      pillClass: "activity-trail-pill--pending",
    };
  }
  return {
    label: status || "—",
    pillClass: "",
  };
};

/** Employee RSVP uses natural copy; SA Mark As keeps "Changed status to". */
export const activityTrailStatusLine = (entry) => {
  const name = entry?.actorName || "—";
  if (entry?.source === "employee" && entry?.status === "INVITE_REJECTED") {
    return { mode: "rsvp", text: `${name} rejected the invite` };
  }
  if (entry?.source === "employee" && entry?.status === "INVITE_ACCEPTED") {
    return { mode: "rsvp", text: `${name} accepted the invite` };
  }
  return { mode: "changed", text: "Changed status to" };
};

const sortNewestFirst = (entries) =>
  [...entries].sort((a, b) => {
    const aTime = a.at ? new Date(a.at).getTime() : 0;
    const bTime = b.at ? new Date(b.at).getTime() : 0;
    return bTime - aTime;
  });

const mapHistoryEntry = (entry, index) => {
  const actorName = entry?.actor?.name || "—";
  return {
    id: entry?._id || `${entry?.toStatus || "entry"}-${entry?.at || index}`,
    actorName,
    initials: getActivityTrailInitials(actorName),
    at: entry?.at || null,
    status: entry?.toStatus,
    source: entry?.source || "admin",
    comment:
      entry?.toStatus === "INVITE_REJECTED"
        ? String(entry?.comment || "").trim()
        : "",
  };
};

/** Legacy scalar fallback when statusHistory was never written. */
const legacyTrailFromRow = (row) => {
  if (!["INVITE_ACCEPTED", "INVITE_REJECTED"].includes(row?.status)) {
    return [];
  }
  const isReject = row.status === "INVITE_REJECTED";
  const isAdminReject = Boolean(isReject && row.rejectionNotedBy?.name);
  const actorName =
    (isAdminReject && row.rejectionNotedBy?.name) ||
    row.employee?.name ||
    "—";
  return [
    {
      id: `${row._id}-legacy`,
      actorName,
      initials: getActivityTrailInitials(actorName),
      at: row.respondedOn || null,
      status: row.status,
      source: isAdminReject ? "admin" : "employee",
      comment: isReject ? String(row.rejectionReason || "").trim() : "",
    },
  ];
};

/** Build newest-first trail entries for a single employee row. */
export const buildEmployeeActivityTrail = (row) => {
  if (!row) return [];
  const history = Array.isArray(row.statusHistory) ? row.statusHistory : [];
  const mapped = history
    .map(mapHistoryEntry)
    .filter((entry) => entry.status);
  if (mapped.length) {
    const sorted = sortNewestFirst(mapped);
    const reason = String(row.rejectionReason || "").trim();
    const reject = sorted.find((entry) => entry.status === "INVITE_REJECTED");
    if (row.status === "INVITE_REJECTED" && reason && reject && !reject.comment) {
      reject.comment = reason;
    }
    return sorted;
  }
  return legacyTrailFromRow(row);
};

/** @deprecated Use buildEmployeeActivityTrail — kept for older imports/tests. */
export const buildInviteActivityTrail = (employees = []) =>
  (Array.isArray(employees) ? employees : []).flatMap((row) =>
    buildEmployeeActivityTrail(row)
  );

export const employeeHasActivityTrail = (row) =>
  buildEmployeeActivityTrail(row).length > 0;

export const ActivityTrailModal = ({ open, onClose, entries = [] }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="activity-trail-title"
    >
      <Box sx={modalStyle} className="activity-trail-modal">
        <header className="activity-trail-modal__header">
          <h2 id="activity-trail-title">Activity Trail</h2>
          <button
            type="button"
            className="activity-trail-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </header>

        <div className="activity-trail-modal__body">
          {entries.length === 0 ? (
            <p className="activity-trail-modal__empty">
              No acceptance or rejection activity yet.
            </p>
          ) : (
            <ul className="activity-trail-list">
              {entries.map((entry) => {
                const statusMeta = activityTrailStatusMeta(entry.status);
                const statusLine = activityTrailStatusLine(entry);
                return (
                  <li key={entry.id} className="activity-trail-list__item">
                    <div className="activity-trail-list__rail" aria-hidden>
                      <span className="activity-trail-list__avatar">
                        {entry.initials}
                      </span>
                    </div>
                    <div className="activity-trail-list__content">
                      <div className="activity-trail-list__meta">
                        <span className="activity-trail-list__name">
                          {entry.actorName}
                        </span>
                        <span className="activity-trail-list__time">
                          {formatActivityTrailAt(entry.at)}
                        </span>
                      </div>
                      <div className="activity-trail-list__card">
                        <p className="activity-trail-list__status-line">
                          {statusLine.mode === "rsvp" ? (
                            statusLine.text
                          ) : (
                            <>
                              {statusLine.text}{" "}
                              <span
                                className={`activity-trail-pill ${statusMeta.pillClass}`}
                              >
                                {statusMeta.label}
                              </span>
                            </>
                          )}
                        </p>
                        {entry.comment ? (
                          <p className="activity-trail-list__comment">
                            {entry.comment}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Box>
    </Modal>
  );
};
